import React, { useState } from 'react';
import { FaShoppingCart, FaUser, FaHeart, FaBars, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCartStore } from '../context/CartStore';
import { useFavoritesStore } from '../context/FavoritesStore';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const { user, logout } = useAuth();
  const cartItems = useCartStore((state) => state.items);
  const favorites = useFavoritesStore((state) => state.favorites);
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleLogout = () => {
    logout();
    setShowMenu(false);
    setShowMobileMenu(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-deepDark/80 backdrop-blur-md border-b border-white/10 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="text-gold text-2xl font-serif font-bold tracking-widest hover:text-white transition-colors duration-300 flex-shrink-0">
          LuxeJewels
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex gap-8 flex-1 justify-center">
          {['All Products', 'Rings', 'Necklaces', 'Bracelets'].map((item) => (
            <Link 
              key={item}
              to={item === 'All Products' ? '/products' : `/products?category=${item}`} 
              className="text-textLight hover:text-gold transition-colors duration-300 text-sm tracking-widest uppercase font-light"
            >
              {item}
            </Link>
          ))}
          <Link to="/delivery" className="text-gold font-semibold hover:text-white transition-colors duration-300 text-sm tracking-widest uppercase">
            Live Tracking
          </Link>
        </nav>

        {/* Desktop Right Icons */}
        <div className="hidden lg:flex items-center gap-6">
          <Link to="/favorites" className="relative text-textLight hover:text-gold transition-colors duration-300 group">
            <FaHeart className="text-xl group-hover:scale-110 transition-transform" /> 
            {favorites.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-gold text-deepDark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </Link>
          
          <Link to="/cart" className="relative text-textLight hover:text-gold transition-colors duration-300 group">
            <FaShoppingCart className="text-xl group-hover:scale-110 transition-transform" /> 
            {cartItems.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-gold text-deepDark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {cartItems.length}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button 
                className="flex items-center gap-2 text-textLight hover:text-gold transition-colors duration-300"
                onClick={() => setShowMenu(!showMenu)}
              >
                <FaUser className="text-lg" /> <span className="text-sm tracking-wider font-light">{user.firstName}</span>
              </button>
              <AnimatePresence>
                {showMenu && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-4 w-56 bg-surfaceDark border border-white/10 shadow-2xl py-2 z-50 rounded-sm"
                  >
                    <Link to="/profile" className="block px-6 py-3 text-textLight hover:text-gold hover:bg-white/5 transition-colors text-sm tracking-wider">My Profile</Link>
                    <Link to="/orders" className="block px-6 py-3 text-textLight hover:text-gold hover:bg-white/5 transition-colors text-sm tracking-wider">My Orders</Link>
                    <Link to="/delivery" className="block px-6 py-3 text-textLight hover:text-gold hover:bg-white/5 transition-colors text-sm tracking-wider">Track Delivery</Link>
                    <div className="border-t border-white/10 my-1"></div>
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-6 py-3 text-textLight hover:text-gold hover:bg-white/5 transition-colors text-sm tracking-wider"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex gap-4">
              <Link to="/login" className="text-textLight hover:text-gold transition-colors text-sm tracking-widest uppercase font-light">Login</Link>
              <Link to="/register" className="text-gold font-semibold hover:text-white transition-colors text-sm tracking-widest uppercase">Register</Link>
            </div>
          )}
        </div>

        {/* Mobile Right Icons & Menu Button */}
        <div className="flex lg:hidden items-center gap-4">
          <Link to="/cart" className="relative text-textLight hover:text-gold transition">
            <FaShoppingCart className="text-xl" />
            {cartItems.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-gold text-deepDark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {cartItems.length}
              </span>
            )}
          </Link>
          <button 
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="text-textLight hover:text-gold transition text-xl"
          >
            {showMobileMenu ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-surfaceDark border-t border-white/10 overflow-hidden"
          >
            <nav className="max-w-7xl mx-auto px-4 py-6 space-y-4">
              {['All Products', 'Rings', 'Necklaces', 'Bracelets'].map((item) => (
                <Link 
                  key={item}
                  to={item === 'All Products' ? '/products' : `/products?category=${item}`} 
                  className="block text-textLight hover:text-gold transition-colors text-sm tracking-widest uppercase font-light"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {item}
                </Link>
              ))}
              <Link 
                to="/delivery" 
                className="block text-gold hover:text-white transition-colors text-sm tracking-widest uppercase font-semibold"
                onClick={() => setShowMobileMenu(false)}
              >
                Live Tracking
              </Link>
              
              <div className="border-t border-white/10 my-4 pt-4 space-y-4">
                {user ? (
                  <>
                    <Link to="/profile" className="block text-textLight hover:text-gold transition-colors text-sm tracking-wider" onClick={() => setShowMobileMenu(false)}>My Profile</Link>
                    <Link to="/orders" className="block text-textLight hover:text-gold transition-colors text-sm tracking-wider" onClick={() => setShowMobileMenu(false)}>My Orders</Link>
                    <button onClick={handleLogout} className="w-full text-left text-textLight hover:text-gold transition-colors text-sm tracking-wider">Logout</button>
                  </>
                ) : (
                  <div className="flex gap-4">
                    <Link to="/login" className="btn btn-outline text-xs flex-1 text-center" onClick={() => setShowMobileMenu(false)}>Login</Link>
                    <Link to="/register" className="btn btn-primary text-xs flex-1 text-center" onClick={() => setShowMobileMenu(false)}>Register</Link>
                  </div>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
